import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, MessageCircle, ShieldCheck, Truck } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import ChapterHeading from "@/components/ChapterHeading";
import { getProduct, getProducts } from "@/lib/api";
import { waProduct, waEncomenda } from "@/lib/whatsapp";

const EASE = [0.22, 1, 0.36, 1];

const Produto = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setActiveImg(0);
    getProduct(id)
      .then((p) => {
        setProduct(p);
        document.title = `${p.nome} | DM IMPORTS`;
        getProducts({ marca: p.marca })
          .then((list) => setRelated(list.filter((r) => r.id !== p.id).slice(0, 4)))
          .catch(() => {});
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-5 text-center" data-testid="product-not-found">
        <p className="font-display text-2xl font-extrabold uppercase tracking-tight">Produto não encontrado</p>
        <p className="mt-3 max-w-sm text-sm text-zinc-400">Esse modelo pode ter esgotado. Fale com a DM Imports e consulte disponibilidade.</p>
        <Link to="/catalogo" data-testid="product-not-found-back" className="mt-8 inline-flex items-center gap-2 rounded-full bg-offwhite px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-obsidian">
          <ArrowLeft className="h-4 w-4" /> Ver catálogo
        </Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="product-loading">
        <p className="text-sm text-zinc-500">Carregando modelo...</p>
      </div>
    );
  }

  const gallery = [product.imagem, ...(product.galeria || [])].filter(Boolean);
  const isReady = product.disponibilidade === "pronta_entrega";

  return (
    <div data-testid="product-page" className="mx-auto max-w-7xl px-5 pb-24 pt-28 sm:px-8 sm:pt-32">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <Link to="/catalogo" data-testid="product-back-link" className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-zinc-500 transition-colors hover:text-offwhite">
          <ArrowLeft className="h-4 w-4" /> Voltar ao catálogo
        </Link>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
        className="mt-8"
      >
        <p data-testid="product-brand" className="text-[11px] font-semibold uppercase tracking-[0.35em] text-silver">
          {product.marca}
        </p>
        <h1 data-testid="product-title" className="mt-2 break-words font-display text-2xl font-extrabold uppercase leading-[1.05] tracking-tight text-offwhite sm:text-4xl lg:text-5xl">
          {product.nome}
        </h1>
      </motion.div>

      <div className="mt-8 grid gap-8 lg:grid-cols-2 lg:gap-16">
        <div>
          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: EASE }}
            className="relative overflow-hidden rounded-2xl border border-white/10 bg-coal"
          >
            <AnimatePresence mode="wait">
              <motion.img
                key={gallery[activeImg]}
                src={gallery[activeImg]}
                alt={`${product.nome} — ${product.marca} | DM Imports`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35 }}
                data-testid="product-gallery-main"
                className="aspect-[4/5] w-full object-cover"
              />
            </AnimatePresence>
            <span
              data-testid="product-status-badge"
              className={`absolute left-4 top-4 rounded-full px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] ${
                isReady ? "bg-offwhite text-obsidian" : "border border-silver/40 bg-obsidian/60 text-silver backdrop-blur-sm"
              }`}
            >
              {isReady ? "Pronta Entrega" : "Encomenda"}
            </span>
          </motion.div>

          {gallery.length > 1 && (
            <div className="no-scrollbar mt-4 flex gap-3 overflow-x-auto pb-1" data-testid="product-gallery-thumbs">
              {gallery.map((img, i) => (
                <button
                  key={img + i}
                  onClick={() => setActiveImg(i)}
                  data-testid={`product-gallery-thumb-${i + 1}`}
                  aria-label={`Foto ${i + 1} do produto`}
                  className={`h-20 w-16 shrink-0 overflow-hidden rounded-lg border transition-colors duration-300 ${
                    activeImg === i ? "border-silver" : "border-white/10 opacity-60 hover:opacity-100"
                  }`}
                >
                  <img src={img} alt={`Miniatura ${i + 1} de ${product.nome}`} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
          className="lg:sticky lg:top-28 lg:self-start"
        >
          <div data-testid="product-meta" className="space-y-2.5 border-y border-white/10 py-5 text-sm">
            {[
              ["Categoria", product.subcategoria || product.categoria],
              ["Cor", product.cor],
              ["Código", product.codigo],
              ["Disponibilidade", isReady ? "Pronta Entrega — envio imediato" : "Sob encomenda"],
            ].filter(([, v]) => v).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4">
                <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">{k}</span>
                <span className="text-right text-offwhite">{v}</span>
              </div>
            ))}
          </div>

          {product.descricao && (
            <p data-testid="product-description" className="mt-6 text-sm leading-relaxed text-zinc-400 sm:text-base">
              {product.descricao}
            </p>
          )}

          <a
            href={waProduct(product.nome, product.codigo)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="product-whatsapp-button"
            className="mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-offwhite px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-obsidian transition-transform duration-300 hover:scale-[1.02]"
          >
            <MessageCircle className="h-5 w-5" />
            Tenho interesse — chamar no WhatsApp
          </a>
          <p className="mt-3 text-center text-[11px] uppercase tracking-[0.2em] text-zinc-500">
            Valor e disponibilidade confirmados no WhatsApp
          </p>

          {!isReady && (
            <a
              href={waEncomenda()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="product-order-button"
              className="mt-4 flex w-full items-center justify-center gap-3 rounded-full border border-silver/40 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-offwhite transition-colors duration-300 hover:bg-offwhite hover:text-obsidian"
            >
              Fazer encomenda deste modelo
            </a>
          )}

          <div className="mt-8 grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-white/10 bg-coal p-4">
              <Truck className="h-5 w-5 text-silver" />
              <p className="mt-2 text-xs font-bold uppercase tracking-[0.15em]">{isReady ? "Envio imediato" : "Encomenda"}</p>
              <p className="mt-1 text-[11px] leading-relaxed text-zinc-500">Combine envio e pagamento direto com a equipe.</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-coal p-4">
              <ShieldCheck className="h-5 w-5 text-silver" />
              <p className="mt-2 text-xs font-bold uppercase tracking-[0.15em]">Original DM</p>
              <p className="mt-1 text-[11px] leading-relaxed text-zinc-500">Curadoria de produtos importados e nacionais.</p>
            </div>
          </div>
        </motion.div>
      </div>

      {related.length > 0 && (
        <section className="mt-24" data-testid="related-products">
          <ChapterHeading num="+" eyebrow="Continue explorando" title="Você também pode gostar" testid="related-heading" />
          <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
            {related.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default Produto;
